import { z } from "zod";
import type { SemanticColors } from "@/constants/SemanticColors";
import type {
    Addresses as Address,
    Customers as Customer,
    Jobs as Job,
} from "@/types/pbSchema";
import { hexToRgba } from "@/lib/color-utils";
import { addressesSchema, customersSchema, jobsSchema } from "./pbZodSchema";

export enum JobStatus {
    PENDING = "PENDING",
    ACTIVE = "ACTIVE",
    PAUSED = "PAUSED",
    COMPLETED = "COMPLETED",
    CANCELED = "CANCELED",
}

// Canonical display order for job statuses, shared by the form selector and the
// list filter menu so they stay in sync.
export const JOB_STATUS_ORDER = [
    JobStatus.ACTIVE,
    JobStatus.PAUSED,
    JobStatus.PENDING,
    JobStatus.COMPLETED,
    JobStatus.CANCELED,
] as const;

export type { Job, Customer, Address };

export type JobWithRelations = Job & {
    expand?: {
        customer?: Customer;
        address?: Address;
    };
};

export type JobWithCustomer = Job & {
    expand: { customer: Customer };
};
export type JobWithAddress = Job & {
    expand: { address: Address };
};

export type InsertJobData = Omit<Job, "id" | "created" | "updated"> & {
    address?: Omit<Address, "id" | "created" | "updated">;
};

export type UpdateJobData = Omit<Job, "created" | "updated" | "org"> & {
    address?: Omit<Address, "created" | "updated">;
};

export interface DeleteJobDialogState {
    open: boolean;
    state: {
        jobId: string;
        jobIdentifier: string;
        organizationId: string;
    } | null;
}

export interface CreateJobDialogState {
    open: boolean;
    isLoading: boolean;
}

export interface EditJobDialogState {
    open: boolean;
    job: Job | null;
    isLoading: boolean;
}

const JOB_STATUS_LABELS: Record<JobStatus, string> = {
    [JobStatus.PENDING]: "Pending",
    [JobStatus.ACTIVE]: "In Progress",
    [JobStatus.PAUSED]: "Paused",
    [JobStatus.COMPLETED]: "Completed",
    [JobStatus.CANCELED]: "Cancelled",
};

export function getJobStatusDisplay(status: JobStatus): string {
    return JOB_STATUS_LABELS[status] ?? "Unknown";
}

// Resolves the badge text/background colors for a job status from the active
// (light/dark-aware) semantic palette. Background is a translucent tint of the
// text color so it adapts to either theme without extra palette entries.
export function getJobStatusColors(
    status: JobStatus,
    semantic: SemanticColors,
): { text: string; background: string } {
    const text = getJobStatusTextColor(status, semantic);
    return { text, background: hexToRgba(text, 0.15) };
}

function getJobStatusTextColor(
    status: JobStatus,
    semantic: SemanticColors,
): string {
    const colors: Record<JobStatus, string> = {
        [JobStatus.PENDING]: semantic.neutral,
        [JobStatus.ACTIVE]: semantic.info,
        [JobStatus.PAUSED]: semantic.warning,
        [JobStatus.COMPLETED]: semantic.success,
        [JobStatus.CANCELED]: semantic.error,
    };
    return colors[status] ?? semantic.neutral;
}

export {
    formatAddress as formatJobAddress,
    isAddressBlank,
} from "@/lib/address";

export const jobStatusSchema = z.nativeEnum(JobStatus);

export const jobSchema = z.object({
    identifier: z.string().trim().min(3, "Job identifier must be at least 3 characters"),
    name: z.string().trim().min(1, "Job name is required"),
    notes: z.string().trim().optional(),
    status: jobStatusSchema,
    startedAt: z.number().optional(),
    finishedAt: z.number().optional(),
});

export const addressSchema = z.object({
    id: z.string().optional(),
    line1: z.string().trim().default(""),
    org: z.string().optional(),
    line2: z.string().trim().default(""),
    city: z.string().trim().default(""),
    stateProvinceCounty: z.string().trim().default(""),
    zipOrPostcode: z.string().trim().default(""),
    country: z.string().trim().default(""),
});

export const customerSchema = z.object({
    id: z.string().optional(),
    name: z.string().min(1, "Customer name is required").trim(),
    identifier: z.string().min(1, "Customer identifier is required").trim(),
});

const OPTIONAL_DATE_STRING = z
    .union([z.string().min(1), z.literal("")])
    .optional()
    .nullable();

export const jobWithRealationsSchema = jobSchema
    .omit({ startedAt: true, finishedAt: true })
    .extend({
        id: z.string().optional(),
        started: OPTIONAL_DATE_STRING,
        completed: OPTIONAL_DATE_STRING,
        tags: z
            .array(z.string().trim().min(1, "Tag cannot be empty"))
            .optional()
            .default([]),
        customer: customerSchema,
        expand: z.object({
            address: addressSchema.omit({ org: true }),
        }),
    })
    .refine(
        (data) => {
            if (data.completed && !data.started) {
                return false;
            }
            return true;
        },
        {
            message: "Start date must be set before completion date",
            path: ["started"],
        },
    )
    .refine(
        (data) => {
            if (data.started && data.completed) {
                const startDate = new Date(data.started);
                const completedDate = new Date(data.completed);
                return startDate <= completedDate;
            }
            return true;
        },
        {
            message: "Start date must be before or equal to completion date",
            path: ["completed"],
        },
    );

export type JobFormData = z.infer<typeof jobWithRealationsSchema>;
